/**
 * The Travel page's filters: trip, traveler, booked or not, below the tracked
 * price, and what kind of watch. They narrow the trip list only; Price drops,
 * Needs you and the tools above it always show everything.
 */

import { markFor, quotes, verdict, type PriceSeries } from "./flightPrices";
import type { Group, Trip, Watch } from "./flightsPage";

export type BookedFilter = "all" | "booked" | "open";
export type KindFilter = "all" | "flight" | "hotel";
export type TripFilter = "all" | number;
/**
 * "all", "mine", or `PERSON_PREFIX` plus a `travelerKey`. The prefix reserves
 * the two words, which are free text a trip could be tagged with.
 */
export type TravelerFilter = string;

export const PERSON_PREFIX = "p:";

export const personValue = (key: string) => PERSON_PREFIX + key;

/**
 * The value every spelling of one person shares: Unicode-normalized, trimmed,
 * inner whitespace collapsed, lower-cased. Free text is the whole point of the
 * tag, so "Robin", "robin" and "Robin " have to land on one filter entry
 * rather than three that each hide two thirds of their trips. NFC is part of
 * that: an accented name pasted in decomposed form looks identical on screen
 * and would otherwise key differently from the same name typed. "" for an
 * untagged trip, which is why the filter never offers "" as a choice.
 */
export const travelerKey = (raw: string | null | undefined): string =>
  (raw ?? "").normalize("NFC").trim().replace(/\s+/g, " ").toLowerCase();

/**
 * Whether a trip's tag satisfies the traveler choice. An untagged trip is his
 * own, which is what "mine" selects: the absence of a tag IS the answer, so
 * nothing has to be written onto a trip to say whose it is.
 */
export function travelerMatches(
  traveler: string | null | undefined,
  f: TravelerFilter,
): boolean {
  if (f === "all") return true;
  const key = travelerKey(traveler);
  if (f === "mine") return key === "";
  return personValue(key) === f;
}

export interface TravelFilter {
  booked: BookedFilter;
  below: boolean;
  kind: KindFilter;
  /** A number is one named trip. */
  trip: TripFilter;
  /** A string is every trip tagged for that person. */
  traveler: TravelerFilter;
}

export const NO_FILTER: TravelFilter = {
  booked: "all", below: false, kind: "all", trip: "all", traveler: "all",
};

/**
 * What a device with nothing remembered opens on: his own trips. Separate from
 * NO_FILTER, which stays the true identity the pills and `isFiltered` compare
 * against, so "Mine" reads as the filter it is rather than as no filter.
 */
export const DEFAULT_FILTER: TravelFilter = { ...NO_FILTER, traveler: "mine" };

export const isFiltered = (f: TravelFilter) =>
  f.booked !== NO_FILTER.booked || f.below !== NO_FILTER.below ||
  f.kind !== NO_FILTER.kind || f.trip !== NO_FILTER.trip ||
  f.traveler !== NO_FILTER.traveler;

export interface TravelerOption {
  /** The stored filter value, already prefixed. */
  value: string;
  label: string;
}

/**
 * The people trips are tagged for, one entry per person. The spelling on the
 * oldest trip wins the label, so the list reads the way you first wrote it
 * while still collapsing the variants onto one choice. Ordered by trip id
 * rather than by the caller's order, which is the trip list sorted by watch
 * date: taking that order would let the label flip between "Robin" and
 * "robin" whenever a watch is added or rescheduled.
 */
export function travelerOptions(trips: Trip[]): TravelerOption[] {
  const byKey = new Map<string, string>();
  for (const trip of [...trips].sort((a, b) => a.id - b.id)) {
    const key = travelerKey(trip.traveler);
    if (!key || byKey.has(key)) continue;
    byKey.set(key, (trip.traveler ?? "").trim());
  }
  return [...byKey]
    .map(([key, label]) => ({ value: personValue(key), label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * The watches whose trip is tagged for the chosen person. Counts run over
 * these, because the traveler lives on the trip and cannot be read off a
 * watch: without this the booked and below tallies would describe everyone
 * while the list on screen showed one person.
 */
export function forTraveler(
  watches: Watch[],
  trips: Trip[],
  traveler: TravelerFilter,
): Watch[] {
  if (traveler === "all") return watches;
  const ids = new Set(
    trips.filter((t) => travelerMatches(t.traveler, traveler)).map((t) => t.id),
  );
  return watches.filter((w) => ids.has(w.trip_id));
}

/** A search is never booked; only flights and hotels carry the flag. */
export const isBooked = (w: Watch) => w.kind !== "query" && Boolean(w.fields.booked);

/**
 * Whether any headline price is BELOW what the watch measures against: the
 * price paid for a booking, the target for anything still being shopped. The
 * same rule that turns a row's verdict green, so the filter and the row never
 * disagree. Equal is not below, so a row reading "Same as paid" is not counted.
 * A drop too small for site-checker to email is still below, so it counts.
 */
export function belowTracked(w: Watch, series: PriceSeries[] | undefined): boolean {
  return quotes(series).some((q) => {
    const tone = verdict(q, markFor(w, q.axis))?.tone;
    return tone === "good" || tone === "floor";
  });
}

export function matches(
  w: Watch,
  f: TravelFilter,
  prices: Record<string, PriceSeries[]>,
): boolean {
  if (f.trip !== "all" && w.trip_id !== f.trip) return false;
  if (f.kind === "flight" && w.kind === "hotel") return false;
  if (f.kind === "hotel" && w.kind !== "hotel") return false;
  if (f.booked === "booked" && !isBooked(w)) return false;
  if (f.booked === "open" && isBooked(w)) return false;
  return !f.below || belowTracked(w, prices[w.watch_id]);
}

/**
 * The trip list under a filter. Unfiltered it is returned as is, so a trip
 * with nothing in it yet still shows; filtered, a trip with no match is hidden.
 *
 * A group's `start` and `end` describe the TRIP, never the rows left after
 * filtering, so the header agrees with the booking count and paid total beside
 * it, which describe the trip too. `tripGroups` returns groups and watches
 * already in date order and filtering preserves it, so nothing re-sorts here.
 */
export function filterGroups(
  groups: Group[],
  f: TravelFilter,
  prices: Record<string, PriceSeries[]>,
): Group[] {
  if (!isFiltered(f)) return groups;
  const rowsAreFiltered = f.booked !== "all" || f.below || f.kind !== "all";
  return groups.flatMap((g): Group[] => {
    if (f.trip !== "all" && g.trip.id !== f.trip) return [];
    if (!travelerMatches(g.trip.traveler, f.traveler)) return [];
    if (!rowsAreFiltered) return [g];
    const watches = g.watches.filter((w) => matches(w, f, prices));
    return watches.length === 0 ? [] : [{ ...g, watches }];
  });
}

/** How many watches each choice would show, given the other choices as set. */
/**
 * The groups whose trip matches the traveler choice, ignoring every other
 * filter. The headline counts describe whose trips are being looked at, not
 * what the rest of the filter bar narrowed, so they stop short of the others.
 */
export function groupsForTraveler(groups: Group[], traveler: TravelerFilter): Group[] {
  if (traveler === "all") return groups;
  return groups.filter((g) => travelerMatches(g.trip.traveler, traveler));
}

/**
 * Whether the filter hid any trip or watch. Trips as well as watches: a trip
 * whose last watch was archived is an empty group, so hiding it moves no watch
 * count and would otherwise disappear with nothing on screen saying why.
 */
export function hidesAnything(groups: Group[], shown: Group[]): boolean {
  const watches = (gs: Group[]) => gs.reduce((n, g) => n + g.watches.length, 0);
  return shown.length !== groups.length || watches(shown) !== watches(groups);
}

export function filterCounts(
  watches: Watch[],
  f: TravelFilter,
  prices: Record<string, PriceSeries[]>,
) {
  const count = (over: Partial<TravelFilter>) =>
    watches.filter((w) => matches(w, { ...f, ...over }, prices)).length;
  return {
    booked: {
      all: count({ booked: "all" }),
      booked: count({ booked: "booked" }),
      open: count({ booked: "open" }),
    },
    below: count({ below: true }),
  };
}

const STORAGE_KEY = "wallet-portal.travel-filter.v2";

/**
 * The pre-v2 key. Read, never written: a PWA tab can run the previous bundle for
 * about a day, and that bundle takes a prefixed value literally.
 */
const LEGACY_STORAGE_KEY = "wallet-portal.travel-filter";

/**
 * The filter this device last used, or his own trips on a first visit. Read
 * defensively, like the which-card fold: storage throws in some private modes,
 * and anything stored but unrecognised widens to NO_FILTER rather than hiding
 * trips behind a filter he did not choose.
 */
/**
 * A stored traveler value, or null when it is not one this module accepts. Which
 * key it came from decides the format: the legacy key predates both reserved
 * words, so a "mine" there is a person whose tag normalized to that word rather
 * than the choice meaning his own trips.
 */
function storedTraveler(value: unknown, legacy: boolean): TravelerFilter | null {
  if (value === undefined || value === null || value === "all") return "all";
  if (typeof value !== "string") return null;
  if (legacy) {
    const key = travelerKey(value);
    return key ? personValue(key) : null;
  }
  if (value === "mine") return "mine";
  // Only a value this module wrote: the prefix exactly as `personValue` spells
  // it, then a non-empty key that is not itself prefixed and survives
  // re-normalizing. Anything else widens, rather than becoming a value no trip
  // can match.
  if (!value.startsWith(PERSON_PREFIX)) return null;
  const key = travelerKey(value.slice(PERSON_PREFIX.length));
  if (!key || key.startsWith(PERSON_PREFIX)) return null;
  return personValue(key) === value ? value : null;
}

export function readTravelFilter(): TravelFilter {
  try {
    const current = localStorage.getItem(STORAGE_KEY);
    const stored = current ?? localStorage.getItem(LEGACY_STORAGE_KEY);
    if (stored === null) return DEFAULT_FILTER;
    const raw = JSON.parse(stored) as Partial<TravelFilter> | null;
    if (!raw || typeof raw !== "object") return NO_FILTER;
    const booked = (["all", "booked", "open"] as const).find((v) => v === raw.booked);
    const kind = (raw.kind as unknown) === "query"
      ? "flight"
      : (["all", "flight", "hotel"] as const).find((v) => v === raw.kind);
    if (!booked || !kind || typeof raw.below !== "boolean") return NO_FILTER;
    let trip: TripFilter;
    if (raw.trip === undefined || raw.trip === "all") trip = "all";
    else if (raw.trip === null) trip = "all";
    else if (typeof raw.trip === "number" && Number.isInteger(raw.trip) && raw.trip > 0) trip = raw.trip;
    else return NO_FILTER;
    const traveler = storedTraveler(raw.traveler, current === null);
    if (traveler === null) return NO_FILTER;
    return { booked, below: raw.below, kind, trip, traveler };
  } catch {
    return NO_FILTER;
  }
}

export function writeTravelFilter(f: TravelFilter): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(f));
  } catch {
    // Not remembering a filter is not worth an error on screen.
  }
}
